import React, { useState } from 'react';

import { Button, Input, showToast } from '@attentive-platform/stem-ui';

import { MODAL_MODE } from './constants';
import MembersTable from './MembersTable';
import { FormContainer } from '../../styled';
import MemberFormModal from './MemberFormModal';
import RemoveMemberModal from './RemoveMemberModal';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { BID_TEAM_ENDPOINT, DELETE_BID_TEAM_MEMBER } from '../../urls';

const parseMembers = responseData => {
  const raw = Array.isArray(responseData) ? responseData : responseData?.emails || [];
  return raw.map(item => ({
    id: item.id,
    name: item.name,
    email: item.email,
    role: item.bid_role,
    member_since: item.created_at
  }));
};

const fetchMembers = async id => {
  const response = await fetchApiData({
    apiRequest: 'GET',
    endpoint: BID_TEAM_ENDPOINT,
    params: { company_id: id }
  });

  if (response.error) {
    return { error: response.error };
  }

  return { members: parseMembers(response?.data) };
};

const BidTeamManagement = () => {
  const [email, setEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [proceedLoading, setProceedLoading] = useState(false);

  const refreshMembers = async id => {
    const result = await fetchMembers(id);
    if (result.error) {
      showToast(result.error, 'error');
      return false;
    }
    setMembers(result.members);
    return true;
  };

  const handleProceed = async () => {
    if (!email) {
      showToast('Please enter a company ID', 'error');
      return;
    }

    setProceedLoading(true);

    try {
      const loaded = await refreshMembers(email);
      if (!loaded) return;

      setCompanyId(email);
      showToast('Team members fetched successfully', 'success');
    } catch (error) {
      showToast('Failed to fetch team members', 'error');
    } finally {
      setProceedLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setMembers([]);
    setCompanyId(null);
    showToast('Form cleared successfully', 'success');
  };

  const handleOpenAddModal = () => {
    setModalConfig({ mode: MODAL_MODE.ADD, currentEmail: '' });
  };

  const handleOpenTransferModal = member => {
    setModalConfig({ mode: MODAL_MODE.TRANSFER, currentEmail: member.email, role: member.role });
  };

  const handleModalSubmit = async ({ newEmail, bidRole, associatedAdmin }, resetForm) => {
    setModalLoading(true);

    try {
      const payload = {
        new_user_email: newEmail,
        bid_role: bidRole,
        company_id: companyId
      };

      if (modalConfig.mode === MODAL_MODE.TRANSFER) {
        payload.current_user_email = modalConfig.currentEmail;
      }

      if (associatedAdmin) {
        payload.associated_admin_email = associatedAdmin;
      }

      const response = await fetchApiData({
        endpoint: BID_TEAM_ENDPOINT,
        apiRequest: 'POST',
        data: payload
      });

      if (response.error) {
        showToast(response.error || 'Operation failed', 'error');
        return;
      }

      const successMsg =
        modalConfig.mode === MODAL_MODE.TRANSFER ? 'Seat transferred successfully' : 'Invite sent successfully';
      showToast(successMsg, 'success');
      await refreshMembers(companyId);
      resetForm();
      setModalConfig(null);
    } catch (error) {
      showToast('Operation failed', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveMember = index => {
    const member = members[index];
    setMemberToDelete({ index, ...member });
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    const { index, id } = memberToDelete;

    if (!id) {
      setMembers(prev => prev.filter((_, i) => i !== index));
      setMemberToDelete(null);
      return;
    }

    setDeleteLoading(true);

    try {
      const response = await fetchApiData({
        endpoint: interpolate(DELETE_BID_TEAM_MEMBER, [id]),
        apiRequest: 'POST',
        data: { company_id: companyId }
      });

      if (response.error) {
        showToast(response.error || 'Failed to remove team member', 'error');
        return;
      }

      showToast('Team member removed successfully', 'success');
      const refreshed = await refreshMembers(companyId);
      if (!refreshed) {
        setMembers(prev => prev.filter(m => m.id !== id));
      }
    } catch (error) {
      showToast('Failed to remove team member', 'error');
    } finally {
      setDeleteLoading(false);
      setMemberToDelete(null);
    }
  };

  const isEmailDisabled = !!companyId;
  const buttonText = companyId ? 'Clear' : 'Proceed';
  const handleEmailAction = companyId ? handleClear : handleProceed;

  return (
    <FormContainer>
      <div className='border border-gray-200 rounded-xl p-6 bg-white shadow-sm'>
        <h2 className='text-xl font-semibold text-gray-900 mb-4'>Bid Team Management</h2>

        <div className='mb-4'>
          <label className='block text-sm text-gray-700 mb-2 text-left'>Company ID</label>
          <div className='flex gap-4'>
            <Input
              value={email}
              className='w-full'
              disabled={isEmailDisabled}
              placeholder='Enter company ID'
              onChange={e => setEmail(e.target.value)}
            />
            <Button color='primary' onClick={handleEmailAction} loading={proceedLoading}>
              {buttonText}
            </Button>
          </div>
        </div>

        <MembersTable
          members={members}
          disabled={!companyId}
          onAdd={handleOpenAddModal}
          onRemove={handleRemoveMember}
          onTransfer={handleOpenTransferModal}
        />
      </div>

      {memberToDelete && (
        <RemoveMemberModal
          show={!!memberToDelete}
          loading={deleteLoading}
          onConfirm={handleConfirmDelete}
          onClose={() => setMemberToDelete(null)}
        />
      )}

      {modalConfig && (
        <MemberFormModal
          show={!!modalConfig}
          loading={modalLoading}
          mode={modalConfig?.mode}
          members={members}
          onSubmit={handleModalSubmit}
          initialRole={modalConfig?.role}
          onClose={() => setModalConfig(null)}
          currentEmail={modalConfig?.currentEmail}
        />
      )}
    </FormContainer>
  );
};

export default BidTeamManagement;
